import { Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { addMonths, endOfMonth, isBefore } from 'date-fns';
import { Subject, takeUntil } from 'rxjs';
import { CalendarTableHelper } from 'server/shared/helpers/calendarTableHelper';
import { startOfDayUtc } from 'server/shared/helpers/startOfDayUtc';
import { formatUserName } from 'server/shared/helpers/user/formatUserName';
import { DepartmentAvailabilityCountResult } from 'server/shared/models/availabilityCountsResult';
import { AvailabilityListUser } from 'server/shared/models/availabilityListUser';
import { Customer } from 'server/shared/models/customer';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { PoolerUser } from 'server/shared/models/user';
import { UserAvailability } from 'server/shared/models/userAvailability';
import { CustomerService } from '../../../services/customer.service';
import {
  AvailabilityEditDialogComponent,
  EditAvailabilityDialogData,
} from '../availability-edit-dialog/availability-edit-dialog.component';

@Component({
  selector: 'app-user-availability-calendar',
  templateUrl: './user-availability-calendar.component.html',
  styleUrls: [
    './user-availability-calendar.component.scss',
    '../../../shared/modules/custom-date-table/date-table-shared-styles.scss',
  ],
})
export class UserAvailabilityCalendarComponent implements OnInit, OnDestroy {
  destroyed$ = new Subject<void>();

  @Input() isDispoEditingPooler?: boolean;

  startAvailability = startOfDayUtc(new Date()); // startOfDayUtc( endOfMonth( addMonths( new Date(), 1 ) ) ); // endOfMonth( addMonths( new Date(), 1 ) );
  endAvailability = endOfMonth(addMonths(this.startAvailability, 2)); // endOfMonth( this.startAvailability );
  calendarTableHelper = new CalendarTableHelper(
    this.startAvailability,
    this.endAvailability,
  );
  listPreparedUser?: AvailabilityListUser;
  _user?: PoolerUser;
  @Input() set user(user: PoolerUser) {
    if (user) {
      this._user = user;
    }
  }

  departmentAvailabilities: DepartmentAvailabilityCountResult[] = [];
  shiftEnum = ShiftEnum;
  displayListItemsCount = 5;
  dispoCustomer?: Customer;

  constructor(
    private ngZone: NgZone,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private customerService: CustomerService,
  ) {
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.ngZone.run(() => {
          this.dispoCustomer = customer;
        });
      });
  }

  // Add a method to format user names based on customer preferences
  formatUserName(user: PoolerUser) {
    return formatUserName(user, this.dispoCustomer);
  }

  async ngOnInit(): Promise<void> {
    if (this.currentUserId) {
      const dispoCustomer = await this.customerService.readyDispoCustomer;
      const customerInformation =
        this._user?.customerInformation?.[dispoCustomer._id];
      let joinDate = customerInformation?.joinDate;
      let leaveDate = customerInformation?.leaveDate;
      if (joinDate && leaveDate && joinDate > leaveDate) {
        this.calendarTableHelper = new CalendarTableHelper(
          this.startAvailability,
          this.endAvailability,
          new Date(leaveDate),
          new Date(joinDate),
        );
      } else {
        this.calendarTableHelper = new CalendarTableHelper(
          joinDate ? new Date(joinDate) : this.startAvailability,
          leaveDate && isBefore(new Date(leaveDate), this.endAvailability)
            ? new Date(leaveDate)
            : this.endAvailability,
        );
      }
    }
    this.listPreparedUser = this.calendarTableHelper.prepareUserForList(
      Object.assign({}, this._user),
    );
    this.getDepartmentAvailabilityCounts();
  }

  private get currentUserId(): string {
    return this.route.snapshot.queryParams['userId'];
  }

  getDepartmentAvailabilityCounts() {
    Meteor.call(
      'getDepartmentAvailabilityCounts',
      this.listPreparedUser,
      this.calendarTableHelper.days,
      (error: Meteor.Error, data: any) => {
        if (error) {
          console.error(error);
          return;
        } else {
          this.ngZone.run(() => {
            this.departmentAvailabilities = data;
          });
        }
      },
    );
  }

  availabilitySelected(availability: UserAvailability) {
    if (!this._user) return;
    const data: EditAvailabilityDialogData = {
      availability,
      user: this._user,
    };
    const dialogRef = this.dialog.open(AvailabilityEditDialogComponent, {
      data,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
      }
    });
  }

  displayMoreEntries() {
    this.displayListItemsCount += 5;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
